<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick, watch, shallowRef } from 'vue'
import { IExtendedDegree } from '@shared/types/extensions'
import { formatDate } from '@/utils/dateFormatter'
import { ref as dbRef, remove, get } from 'firebase/database'
import { database } from '@/firebase/config'
import { downloadService } from '@/services/downloadService'
import NewDegreeModal from '@/components/titulos/NewDegreeModal.vue'
import { IDegree } from '@shared/types'
import LoadingState from '@/components/common/LoadingState.vue'
import { EAuthorizationType, EPeriodType, EEntidadFederativa, ETipoEstudioAntecedente, EFundamentoServicioSocial, EGraduationMode, EDocumentType, EDocumentStatus } from '@shared/types/enums'
import BaseModal from '../BaseModal.vue'
// Importar estilos compartidos
import '@/styles/virtualized-components.css'

const props = defineProps<{
	degrees: IExtendedDegree[]
	institution: any
	currentInstitution: string
	testMode: boolean
}>()

const emit = defineEmits<(e: 'degree-removed', value: boolean) => void>()

const db = database
const degrees = shallowRef<IExtendedDegree[]>([])
const loading = ref(false)
const showNewCertificateModal = ref(false)
const selectedDegree = ref<IDegree | undefined>(undefined)
const getInstitutionPath = (path: string) => `institutions/${props.currentInstitution}${props.testMode ? '/testMode' : ''}${path}`

// Estados de carga mejorados
const isInitializing = ref(true)
const isRemoving = ref<string | null>(null) // ID del elemento que se está eliminando
const removingItems = ref<Set<string>>(new Set()) // Conjunto de IDs que se están eliminando
const preparationMessage = ref('Preparando vista...')
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
// Virtualización optimizada
const containerRef = ref<HTMLElement>()
const itemHeight = 200 // Altura estimada de cada tarjeta
const visibleItems = ref<IExtendedDegree[]>([])
const scrollTop = ref(0)
const containerHeight = ref(0)
const startIndex = computed(() => Math.floor(scrollTop.value / itemHeight))
const endIndex = computed(() => Math.min(startIndex.value + Math.ceil(containerHeight.value / itemHeight) + 3, degrees.value.length))

// Throttling para scroll
let scrollTimeout: number | null = null
const SCROLL_THROTTLE = 16 // ~60fps

// Calcular elementos visibles con throttling
const updateVisibleItems = () => {
	if (!degrees.value.length) {
		visibleItems.value = []
		return
	}

	// Usar requestAnimationFrame para evitar bloqueos
	requestAnimationFrame(() => {
		visibleItems.value = degrees.value.slice(startIndex.value, endIndex.value)
	})
}

// Manejar scroll con throttling
const handleScroll = (event: Event) => {
	if (scrollTimeout) return

	scrollTimeout = window.setTimeout(() => {
		const target = event.target as HTMLElement
		scrollTop.value = target.scrollTop
		updateVisibleItems()
		scrollTimeout = null
	}, SCROLL_THROTTLE)
}

// Actualizar altura del contenedor
const updateContainerHeight = () => {
	if (containerRef.value) {
		containerHeight.value = containerRef.value.clientHeight
		updateVisibleItems()
	}
}

// Observar cambios en el array de títulos con debouncing
let degreesUpdateTimeout: number | null = null
const updateOnDegreesChange = () => {
	if (degreesUpdateTimeout) {
		clearTimeout(degreesUpdateTimeout)
	}

	degreesUpdateTimeout = window.setTimeout(() => {
		// Solo actualizar si no hay elementos en proceso de eliminación
		if (removingItems.value.size === 0) {
			degrees.value = props.degrees
			nextTick(() => {
				updateVisibleItems()
			})
		}
	}, 100) // Debounce de 100ms
}

// Edit certificate
const editCertificate = (id: string) => {
	selectedDegree.value = props.degrees.find(degree => degree.id === id)
	showNewCertificateModal.value = true
}

// Remove certificate optimizado
const removeDegree = async (id: string) => {
	if (!confirm('¿Está seguro de eliminar este título?')) return

	try {
		// Marcar el elemento como en proceso de eliminación
		removingItems.value.add(id)
		isRemoving.value = id

		// Actualizar inmediatamente la vista para mostrar el estado de eliminación
		updateVisibleItems()

		const degreeRef = dbRef(db, getInstitutionPath(`/degrees/queue/${id}`))
		await remove(degreeRef)

		// Eliminar inmediatamente de la vista local sin esperar la actualización desde la base de datos
		degrees.value = degrees.value.filter(degree => degree.id !== id)
		updateVisibleItems()

		// Emitir evento de eliminación
		emit('degree-removed', true)

		// Limpiar estados de eliminación
		removingItems.value.delete(id)
		isRemoving.value = null

		// Limpiar completamente después de un tiempo para evitar estados inconsistentes
		setTimeout(() => {
			removingItems.value.clear()
			isRemoving.value = null
		}, 1000)
	} catch (error) {
		console.error('Error removing degree:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al eliminar el título'

		// Limpiar estados en caso de error
		removingItems.value.delete(id)
		isRemoving.value = null
	}
}

// Download degree
const downloadDegree = async (degreeId: string) => {
	try {
		loading.value = true
		const degreeRef = dbRef(db, getInstitutionPath(`/degrees/queue/${degreeId}`))
		const snapshot = await get(degreeRef)

		if (!snapshot.exists()) {
			throw new Error('No se encontró el título')
		}

		const degreeData = snapshot.val()
		const degree = createDegreeObject(degreeId, degreeData)
		await downloadService.downloadDocument(degree, 'título')
	} catch (error) {
		console.error('Error downloading degree:', error)
		showErrorModal.value = true
		errorMessage.value = 'No fue posible descargar el título'
	} finally {
		loading.value = false
	}
}

const createDegreeObject = (id: string, degreeData: any): IExtendedDegree => ({
	id,
	type: EDocumentType.DEGREE,
	data: {
		Profesionista: {
			nombre: degreeData.data?.Profesionista?.nombre || '',
			primerApellido: degreeData.data?.Profesionista?.primerApellido || '',
			segundoApellido: degreeData.data?.Profesionista?.segundoApellido || '',
			curp: degreeData.data?.Profesionista?.curp || '',
			correoElectronico: degreeData.data?.Profesionista?.correoElectronico || ''
		},
		Carrera: {
			nombreCarrera: degreeData.data?.Carrera?.nombreCarrera || '',
			idCarrera: degreeData.data?.Carrera?.idCarrera || '',
			cveCarrera: degreeData.data?.Carrera?.claveCarrera || '',
			clavePlan: degreeData.data?.Carrera?.clavePlan || '',
			idNivelEstudios: degreeData.data?.Carrera?.idNivelEstudios || 0,
			autorizacionReconocimiento: degreeData.data?.Carrera?.autorizacionReconocimiento || '',
			fechaInicio: degreeData.data?.Carrera?.fechaInicio || '',
			fechaTerminacion: degreeData.data?.Carrera?.fechaTerminacion || '',
			idAutorizacionReconocimiento: degreeData.data?.Carrera?.idAutorizacionReconocimiento || EAuthorizationType.RVOE_FEDERAL,
			idTipoPeriodo: degreeData.data?.Carrera?.idTipoPeriodo || EPeriodType.SEMESTRE,
			calificacionMaxima: degreeData.data?.Carrera?.calificacionMaxima || 10,
			calificacionMinima: degreeData.data?.Carrera?.calificacionMinima || 5,
			calificacionMinimaAprobatoria: degreeData.data?.Carrera?.calificacionMinimaAprobatoria || '6.00',
			tipoPeriodo: degreeData.data?.Carrera?.tipoPeriodo || 'SEMESTRE',
			numeroRvoe: degreeData.data?.Carrera?.numeroRvoe || ''
		},
		Antecedente: {
			entidadFederativa: degreeData.data?.Antecedente?.entidadFederativa || '',
			fechaTerminacion: degreeData.data?.Antecedente?.fechaTerminacion || '',
			idEntidadFederativa: degreeData.data?.Antecedente?.idEntidadFederativa || EEntidadFederativa.AGUASCALIENTES,
			idTipoEstudioAntecedente: degreeData.data?.Antecedente?.idTipoEstudioAntecedente || ETipoEstudioAntecedente.LICENCIATURA,
			institucionProcedencia: degreeData.data?.Antecedente?.institucionProcedencia || '',
			noCedula: degreeData.data?.Antecedente?.noCedula || '',
			tipoEstudioAntecedente: degreeData.data?.Antecedente?.tipoEstudioAntecedente || ''
		},
		Expedicion: {
			cumplioServicioSocial: degreeData.data?.Expedicion?.cumplioServicioSocial || 0,
			entidadFederativa: degreeData.data?.Expedicion?.entidadFederativa || '',
			fechaExencionExamenProfesional: degreeData.data?.Expedicion?.fechaExencionExamenProfesional || '',
			fechaExpedicion: degreeData.data?.Expedicion?.fechaExpedicion || '',
			fundamentoLegalServicioSocial: degreeData.data?.Expedicion?.fundamentoLegalServicioSocial || '',
			idFundamentoLegalServicioSocial: degreeData.data?.Expedicion?.idFundamentoLegalServicioSocial || EFundamentoServicioSocial.NO_APLICA,
			idEntidadFederativa: degreeData.data?.Expedicion?.idEntidadFederativa || EEntidadFederativa.CIUDAD_DE_MEXICO,
			idModalidadTitulacion: degreeData.data?.Expedicion?.idModalidadTitulacion || EGraduationMode.POR_PROMEDIO,
			modalidadTitulacion: degreeData.data?.Expedicion?.modalidadTitulacion || ''
		},
		Institucion: {
			cveInstitucion: degreeData.data?.Institucion?.cveInstitucion || '',
			nombreInstitucion: degreeData.data?.Institucion?.nombreInstitucion || ''
		},
		TituloElectronico: {
			folioControl: degreeData.data?.TituloElectronico?.folioControl || '',
			version: degreeData.data?.TituloElectronico?.version || '',
			xmlns: degreeData.data?.TituloElectronico?.xmlns || ''
		},
		numeroControl: degreeData.data?.numeroControl || '',
		insertTime: degreeData.data?.insertTime || Date.now(),
		row: degreeData.data?.row || 0,
		FirmaResponsables: degreeData.data?.FirmaResponsables || {}
	},
	controlNumber: degreeData.controlNumber || '',
	date: degreeData.date ? new Date(degreeData.date).getTime() : Date.now(),
	status: degreeData.status || EDocumentStatus.PENDING,
	warning: degreeData.warning,
	error: degreeData.error,
	selected: false,
	folio: degreeData.folio || '',
	folioControl: degreeData.folioControl,
	xml: degreeData.xml,
	cadenaOriginal: degreeData.cadenaOriginal,
	success: degreeData.success || false,
	studentName: `${degreeData.data?.Profesionista?.nombre || ''} ${degreeData.data?.Profesionista?.primerApellido || ''} ${degreeData.data?.Profesionista?.segundoApellido || ''}`.trim(),
	career: degreeData.data?.Carrera?.nombreCarrera,
	package: degreeData.package,
	xmlAuthenticated: ''
})

// Computed properties for better template organization
const getStudentFullName = (degree: IExtendedDegree) => {
	const nombre = degree?.data?.Profesionista?.nombre || 'Sin nombre'
	const primerApellido = degree?.data?.Profesionista?.primerApellido || 'Sin apellido'
	const segundoApellido = degree?.data?.Profesionista?.segundoApellido || 'Sin apellido'
	return `${nombre} ${primerApellido} ${segundoApellido}`.trim()
}

// Verificar si un elemento está siendo eliminado
const isItemRemoving = (id: string) => removingItems.value.has(id)

// Variables para cleanup
let resizeObserver: ResizeObserver | null = null

// Lifecycle
onMounted(async () => {
	// Simular proceso de inicialización
	preparationMessage.value = 'Cargando títulos...'
	await new Promise(resolve => setTimeout(resolve, 500))

	// Inicializar con los datos actuales
	degrees.value = props.degrees

	preparationMessage.value = 'Configurando vista...'
	await new Promise(resolve => setTimeout(resolve, 300))

	updateContainerHeight()
	updateVisibleItems()
	// Marcar como inicializado
	isInitializing.value = false

	// Resize observer para detectar cambios en el tamaño del contenedor
	resizeObserver = new ResizeObserver(() => {
		updateContainerHeight()
	})

	if (containerRef.value) {
		resizeObserver.observe(containerRef.value)
	}
})

// Cleanup en onUnmounted
onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect()
	}
	if (scrollTimeout) {
		clearTimeout(scrollTimeout)
	}
	if (degreesUpdateTimeout) {
		clearTimeout(degreesUpdateTimeout)
	}
})

// Watch for degrees changes with debouncing
watch(() => props.degrees, updateOnDegreesChange, { deep: true })

// Watcher específico para detectar eliminaciones y actualizar inmediatamente
watch(
	() => props.degrees.length,
	(newLength, oldLength) => {
		if (oldLength && newLength < oldLength) {
			// Solo actualizar si no hay elementos en proceso de eliminación
			if (removingItems.value.size === 0) {
				degrees.value = props.degrees
				updateVisibleItems()
			}
		}
	}
)
</script>

<template>
	<div ref="containerRef" @scroll="handleScroll" class="virtualized-container">
		<!-- Indicador de preparación -->
		<LoadingState v-if="isInitializing" :loading-progress="0" :show-progress="false" title="Cargando títulos..." description="Preparando la vista optimizada para mejor rendimiento" />

		<!-- Loading State -->
		<div v-if="loading" class="loading-state">
			<div class="loader"></div>
			<p class="loading-text">Procesando...</p>
		</div>

		<!-- Empty State -->
		<div v-else-if="degrees.length === 0 && !isInitializing" class="empty-state">
			<VaIcon name="school" class="empty-icon" />
			<h3 class="empty-title">No hay títulos registrados</h3>
			<p class="empty-description">Los títulos aparecerán aquí una vez que sean agregados al sistema.</p>
		</div>

		<!-- Virtualized Degrees Grid -->
		<div v-else class="virtualized-grid">
			<!-- Spacer para mantener el scroll correcto -->
			<div class="virtualized-spacer" :style="{ height: `${startIndex * itemHeight}px` }"></div>

			<!-- Elementos visibles -->
			<div v-for="degree in visibleItems" :key="degree.id" class="virtualized-content">
				<VaCard class="degree-card" :class="{ 'removing': isItemRemoving(degree.id) }" outlined>
					<!-- Overlay de eliminación -->
					<div v-if="isItemRemoving(degree.id)" class="removing-overlay">
						<div class="removing-content">
							<div class="removing-spinner"></div>
							<span>Eliminando...</span>
						</div>
					</div>

					<!-- Card Header -->
					<VaCardTitle class="degree-header">
						<div class="degree-header-content">
							<div class="degree-student-info">
								<VaAvatar icon="person" class="student-avatar" color="info" size="small" />
								<div class="student-details">
									<h4 class="student-name">{{ getStudentFullName(degree) }}</h4>
									<p class="student-career">{{ degree?.data?.Carrera?.nombreCarrera || 'Carrera no especificada' }}</p>
								</div>
							</div>

							<div class="degree-actions" :class="{ 'disabled': isItemRemoving(degree.id) }">
								<VaPopover message="Editar título">
									<VaButton
										round
										color="primary"
										icon="edit"
										@click="editCertificate(degree.id)"
										size="small"
										:disabled="isItemRemoving(degree.id)"
										style="display: flex; align-items: center; justify-content: center" />
								</VaPopover>

								<input type="checkbox" class="custom-checkbox" v-model="degree.selected" :disabled="isItemRemoving(degree.id)" />

								<VaPopover message="Descargar título">
									<VaButton
										round
										color="primary"
										icon="download"
										@click="downloadDegree(degree.id)"
										size="small"
										:disabled="isItemRemoving(degree.id)"
										style="display: flex; align-items: center; justify-content: center" />
								</VaPopover>

								<VaPopover message="Eliminar título">
									<VaButton
										round
										color="danger"
										icon="delete"
										@click="removeDegree(degree.id)"
										size="small"
										:disabled="isItemRemoving(degree.id)"
										style="display: flex; align-items: center; justify-content: center" />
								</VaPopover>
							</div>
						</div>
					</VaCardTitle>

					<!-- Card Content -->
					<VaCardContent class="degree-content">
						<VaDivider class="content-divider" />

						<!-- Degree Information -->
						<div class="degree-info">
							<div class="info-item">
								<VaIcon name="school" class="info-icon" />
								<span class="info-label">Carrera:</span>
								<span class="info-value">{{ degree?.data?.Carrera?.nombreCarrera || 'No especificada' }}</span>
							</div>

							<div class="info-item">
								<VaIcon name="fingerprint" class="info-icon" />
								<span class="info-label">Número de Control:</span>
								<span class="info-value">{{ degree?.data?.numeroControl || 'No especificado' }}</span>
							</div>

							<div class="info-item">
								<VaIcon name="badge" class="info-icon" />
								<span class="info-label">CURP:</span>
								<span class="info-value">{{ degree?.data?.Profesionista?.curp || 'No especificada' }}</span>
							</div>

							<div class="info-item">
								<VaIcon name="event" class="info-icon" />
								<span class="info-label">Fecha de Registro:</span>
								<span class="info-value">{{ degree.date ? formatDate(degree.date) : 'No especificada' }}</span>
							</div>
						</div>

						<!-- Status Badge -->
						<div v-if="!degree.success && !degree.error && !degree.warning" class="status-badge status-pending">
							<VaIcon name="pending" class="status-icon spin" />
							<span>Esperando validación</span>
						</div>

						<!-- Error Alert -->
						<VaAlert v-if="degree.error" color="danger" class="status-alert">
							<template #icon>
								<VaIcon name="error" />
							</template>
							{{ degree.error.message }}
						</VaAlert>

						<!-- Warning Alert -->
						<VaAlert v-if="degree.warning" color="warning" class="status-alert">
							<template #icon>
								<VaIcon name="warning" />
							</template>
							{{ degree.warning.message }}
						</VaAlert>

						<!-- Degree ID -->
						<div class="degree-id" style="text-align: end">
							<sub>{{ degree.id }}</sub>
						</div>
					</VaCardContent>
				</VaCard>
			</div>

			<!-- Spacer final para mantener el scroll correcto -->
			<div class="virtualized-spacer" :style="{ height: `${(degrees.length - endIndex) * itemHeight}px` }"></div>
		</div>

		<!-- Modal -->
		<NewDegreeModal
			v-model="showNewCertificateModal"
			:institution="props.institution"
			:isTestMode="props.testMode"
			:degree="selectedDegree"
			:currentInstitution="props.currentInstitution"
			:isEdit="true" />
	</div>
	<BaseModal v-model="showErrorModal" title="Error" :test-mode="testMode" :message="errorMessage" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.virtualized-content {
	min-height: 320px;
}

/* Estados de eliminación */
.degree-card.removing {
	opacity: 0.6;
	pointer-events: none;
	position: relative;
}
.status-badge {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 2px;
	padding: 8px 16px;
	border-radius: 12px;
	font-size: 10px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.25px;
	margin-bottom: 10px;
}
.status-pending {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}

.removing-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	border-radius: 12px;
}

.removing-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	color: #dc3545;
	font-weight: 600;
}

.removing-spinner {
	width: 24px;
	height: 24px;
	border: 3px solid rgba(220, 53, 69, 0.3);
	border-top: 3px solid #dc3545;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

.degree-actions.disabled {
	opacity: 0.5;
	pointer-events: none;
}

/* Estados de carga */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem;
	gap: 1rem;
}

.loader {
	width: 40px;
	height: 40px;
	border: 4px solid rgba(0, 123, 255, 0.3);
	border-top: 4px solid #007bff;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

.loading-text {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
}

.empty-icon {
	font-size: 4rem;
	color: #6c757d;
	margin-bottom: 1rem;
}

.empty-title {
	color: #495057;
	font-size: 1.5rem;
	margin: 0 0 0.5rem 0;
}

.empty-description {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

/* Animaciones adicionales */
@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.status-icon.spin {
	animation: spin 2s linear infinite;
}
</style>
